import React from 'react';
import { Helmet } from 'react-helmet';
import { Organization } from 'utils';

interface PageMetaProps {
    pageType: 'shop' | 'athlete-start' | 'coach-onboard';
    organization?: Organization | null;
    customTitle?: string;
    customDescription?: string;
    customImage?: string;
}

const PageMeta: React.FC<PageMetaProps> = ({
    pageType,
    organization,
    customTitle,
    customDescription,
    customImage,
}) => {
    const generateTitle = (): string => {
        if (customTitle) return customTitle;

        const orgName = organization?.name;

        switch (pageType) {
            case 'shop':
                return orgName ? `${orgName} Marketplace - 1Tul` : '1Tul Marketplace';
            case 'athlete-start':
                return orgName ? `Join ${orgName} - 1Tul Setup` : '1Tul Account Setup';
            case 'coach-onboard':
                return orgName
                    ? `${orgName} Organization Setup - 1Tul`
                    : 'Create Organization - 1Tul';
            default:
                return '1Tul';
        }
    };

    const generateDescription = (): string => {
        if (customDescription) return customDescription;

        const orgName = organization?.name;

        switch (pageType) {
            case 'shop':
                return orgName
                    ? `Shop ${orgName}'s marketplace on 1Tul - buy Fiyrpods, vests, chargers and other sports performance items here`
                    : '1Tul Marketplace - buy Fiyrpods, vests, chargers and other items here';
            case 'athlete-start':
                return orgName
                    ? `Join ${orgName} on 1Tul - get started with your sports performance analytics account and team setup`
                    : '1Tul Account Setup - sports performance analytics and team management platform';
            case 'coach-onboard':
                return orgName
                    ? `Set up your ${orgName} organization on 1Tul - sports performance analytics platform for coaches and teams`
                    : 'Create your organization on 1Tul - sports performance analytics platform for coaches and teams';
            default:
                return '1Tul - sports performance analytics and marketplace';
        }
    };

    const getImageUrl = (): string => {
        if (customImage) return customImage;
        if (organization?.logo_url) return organization.logo_url;
        return '/1TULlogo512.png';
    };

    const getCurrentPageUrl = (): string => {
        return window.location.href;
    };

    const title = generateTitle();
    const description = generateDescription();
    const imageUrl = getImageUrl();

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />

            {/* Open Graph tags for link sharing */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={getCurrentPageUrl()} />
            <meta property="og:image" content={imageUrl} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />
        </Helmet>
    );
};

export default PageMeta;
